// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import { clientLogger } from '@seedplanner/logger';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '../../lib/base';
import { Help } from '../../lib/help';
import { DASHBOARD_OVERDUE_TASKS_QUERY, DASHBOARD_QUERY } from '../../queries/DashboardQueries';
import type {
  CropHarvestBufferTimeDataQuery,
  CropHarvestBufferTimeDataQueryVariables,
  EditCropHarvestBufferTimeMutation,
  EditCropHarvestBufferTimeMutationVariables,
} from '../../types/__generated__/graphql';
import {
  getDaysFromSeconds,
  getNumericID,
  getSecondsFromDaysStringRequired,
  getTimeRanges,
  validateNumInput,
} from '../../utils';
import { ExitDialogButton } from '../common/button';
import { ErrorView } from '../common/error';
import { PageLoading } from '../common/loading';
import { formatCropDialogTitle } from './formatCropDialogTitle';

const EDIT_CROP_HARVEST_BUFFER_TIME_MUTATION = gql`
  mutation EditCropHarvestBufferTime(
    $crop_id: Int!
    $harvest_buffer_time: Int!
  ) {
    editCropHarvestBufferTime(
      input: {
        crop_id: $crop_id
        harvest_buffer_time: $harvest_buffer_time
      }
    ) {
      updated_crop {
        id
        harvest_buffer_time
        updated_at
      }
      updated_plantings {
        id
        planting_time
        updated_at
      }
      updated_tasks {
        id
        task_time
        updated_at
      }
    }
  }
`;

const CROP_HARVEST_BUFFER_TIME_DATA = gql`
  query CropHarvestBufferTimeData($id: ID!) {
    crop(id: $id) {
      id
      harvest_buffer_time
      max_hold_time
    }
  }
`;

interface Props {
  readonly cropId: string;
  readonly cropName: string;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export default function EditCropHarvestBufferTimeDialog({ isOpen, onClose, onSuccess, cropId, cropName }: Props) {
  const [fetchCrop, { data, error: dataError, loading: cropDataLoading }] = useLazyQuery<
    CropHarvestBufferTimeDataQuery,
    CropHarvestBufferTimeDataQueryVariables
  >(CROP_HARVEST_BUFFER_TIME_DATA, {
    fetchPolicy: 'network-only',
  });
  const [editCropHarvestBuffer, { loading: editCropLoading }] = useMutation<
    EditCropHarvestBufferTimeMutation,
    EditCropHarvestBufferTimeMutationVariables
  >(EDIT_CROP_HARVEST_BUFFER_TIME_MUTATION);
  const { timezone } = useUserContext();
  const { todayStart, sevenDaysFromTodayStart } = getTimeRanges(timezone);

  React.useEffect(() => {
    if (isOpen) {
      void fetchCrop({
        variables: { id: cropId },
        onCompleted: (cropData) => {
          const crop = cropData?.crop;
          if (crop != null) {
            const harvestBufferTime =
              crop.harvest_buffer_time != null ? getDaysFromSeconds(crop.harvest_buffer_time).toString() : '';

            setHarvestBufferTime(harvestBufferTime);
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        },
      });
    }
  }, [isOpen, cropId, fetchCrop]);

  const disableFields = cropDataLoading || editCropLoading;

  const existingHarvestBufferTimeDays =
    data?.crop?.harvest_buffer_time != null ? getDaysFromSeconds(data.crop.harvest_buffer_time) : undefined;
  const existingMaxHoldTime = data?.crop?.max_hold_time != null ? getDaysFromSeconds(data.crop.max_hold_time) : 0;

  const [harvestBufferTime, setHarvestBufferTime] = React.useState('');
  const [harvestBufferTimeValidation, setHarvestBufferTimeValidation] = React.useState('');
  const onChangeHarvestBufferTime = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setHarvestBufferTime(e.target.value);
    // Harvest buffer time must be greater than the shelf life
    const harvestBufferTimeAsNum = Number.parseFloat(e.target.value) || Number.NEGATIVE_INFINITY;
    if (existingMaxHoldTime != null && !(existingMaxHoldTime >= harvestBufferTimeAsNum)) {
      setHarvestBufferTimeValidation('Harvest buffer time must be less than or equal to shelf life');
    } else {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setHarvestBufferTimeValidation(msg),
        validateGTE: 0,
        validateInt: true,
      });
    }
  };
  const onHarvestBufferTimeBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Harvest buffer time must be greater than the shelf life
    const harvestBufferTimeAsNum = Number.parseFloat(e.target.value) || Number.NEGATIVE_INFINITY;
    if (existingMaxHoldTime != null && !(existingMaxHoldTime >= harvestBufferTimeAsNum)) {
      setHarvestBufferTimeValidation('Harvest buffer time must be less than or equal to shelf life');
    } else {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setHarvestBufferTimeValidation(msg),
        validateGTE: 0,
        validateInt: true,
      });
    }
  };

  const onSubmit = (_e?: React.MouseEvent | React.TouchEvent) => {
    void editCropHarvestBuffer({
      refetchQueries: [
        {
          query: DASHBOARD_QUERY,
          variables: {
            todayStart: todayStart.toString(),
            sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
          },
        },
        {
          query: DASHBOARD_OVERDUE_TASKS_QUERY,
          variables: {
            todayStart: todayStart.toString(),
            first: 25,
          },
        },
      ],
      update: (cache, { data }) => {
        const plantingTimesUpdated = (data?.editCropHarvestBufferTime?.updated_plantings?.length ?? 0) > 0;
        const tasksUpdated = (data?.editCropHarvestBufferTime?.updated_tasks?.length ?? 0) > 0;
        cache.modify({
          fields: {
            harvests(existingGroup, { DELETE }) {
              return plantingTimesUpdated ? DELETE : existingGroup;
            },
            taskGroup(existingGroup, { DELETE }) {
              return tasksUpdated ? DELETE : existingGroup;
            },
            task_groups(existingGroup, { DELETE }) {
              return tasksUpdated ? DELETE : existingGroup;
            },
          },
        });
      },
      variables: {
        harvest_buffer_time: getSecondsFromDaysStringRequired(harvestBufferTime),
        crop_id: getNumericID(cropId),
      },
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.editCropHarvestBufferTime != null) {
          enqueueSnackbar('Updated crop harvest buffer', { variant: 'success' });
          onSuccess();
        } else {
          enqueueSnackbar('Error editing crop, no data returned', { variant: 'error', autoHideDuration: 10000 });
        }
        setHarvestBufferTime('');
        setHarvestBufferTimeValidation('');
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        clientLogger.error({ title: 'edit_crop_error', message: errorIn.message });
      },
    });
  };

  const bufferTimeChanged = getSecondsFromDaysStringRequired(harvestBufferTime) !== data?.crop?.harvest_buffer_time;
  const hasInputValidationError: boolean = harvestBufferTimeValidation !== '';
  const requiredFieldsFilled: boolean = harvestBufferTime !== '';
  const isSubmitDisabled: boolean =
    !requiredFieldsFilled || hasInputValidationError || disableFields || !bufferTimeChanged;
  const onEnter = () => {
    if (!isSubmitDisabled) {
      onSubmit();
    }
  };
  const onCancelKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onClose();
    }
  };
  const onSubmitKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (!isSubmitDisabled) {
        onSubmit();
      }
    }
  };

  const newHarvestBufferTimeDays = Number.parseInt(harvestBufferTime);
  const bufferDaysDiff =
    existingHarvestBufferTimeDays === undefined ||
    newHarvestBufferTimeDays === undefined ||
    Number.isNaN(newHarvestBufferTimeDays)
      ? undefined
      : existingHarvestBufferTimeDays - newHarvestBufferTimeDays;

  const form = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={1}>
      <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" gap={2}>
        <TextField
          aria-describedby="harvest-buffer-time"
          id="harvest-buffer-time"
          label="Harvest Buffer Time (days)"
          value={harvestBufferTime}
          onChange={onChangeHarvestBufferTime}
          onBlur={onHarvestBufferTimeBlur}
          onEnter={onEnter}
          subtext={harvestBufferTimeValidation}
          error={harvestBufferTimeValidation !== ''}
          type="number"
          inputMode="numeric"
          numberMin={0}
          hasSubtext
          disabled={disableFields}
        />
        <Help tooltip="This is how many days before the order delivery date that this crop should be harvested. For example, if your order delivery date is on Friday, but you want this crop to be harvested on Thursday, then you would set the harvest buffer time to 1 day. This is optional though and defaults to zero." />
      </Box>
      {(existingHarvestBufferTimeDays !== undefined || existingMaxHoldTime !== undefined) && (
        <Box>
          {existingHarvestBufferTimeDays !== undefined && (
            <Typography variant="body1">Existing Harvest Buffer Time: {existingHarvestBufferTimeDays} days</Typography>
          )}
          {existingMaxHoldTime !== undefined && (
            <Typography variant="body1">Shelf Life: {existingMaxHoldTime} days</Typography>
          )}
          {bufferDaysDiff !== undefined && bufferDaysDiff !== 0 && (
            <Typography variant="body1" sx={{ mt: 1 }}>
              Planned plantings and their tasks will be shifted {bufferDaysDiff > 0 ? 'forward' : 'back'} by{' '}
              {Math.abs(bufferDaysDiff)} days
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{formatCropDialogTitle('Edit Crop Harvest Buffer Time', cropName)}</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>
        {dataError ? <ErrorView error={dataError} /> : cropDataLoading || data?.crop == null ? <PageLoading /> : form}
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onClose}
          onKeyDown={onCancelKeyDown}
        >
          Cancel
        </Button>
        <Button
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
          onKeyDown={onSubmitKeyDown}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
